/**
 * Business QR Print Page
 * /biz/:slug/qr-print
 * QR 코드 프린트용 페이지
 */

import { useRoute, Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Printer, ArrowLeft } from "lucide-react";
import { useState } from "react";

export default function BusinessQRPrint() {
  const [match, params] = useRoute<{ slug: string }>("/biz/:slug/qr-print");

  if (!match || !params) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">유효하지 않은 업체입니다</p>
            <Link href="/business-directory">
              <Button className="mt-4">목록으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const slug = params.slug;

  // Fetch business data
  const { data: business, isLoading } = useQuery<{
    id: string;
    name: string;
    coverImageUrl: string | null;
  }>({
    queryKey: ["/api/businesses", slug],
    queryFn: async () => {
      const businessRes = await fetch(`/api/businesses?slug=${slug}`);
      if (!businessRes.ok) throw new Error("Business not found");
      const businesses = await businessRes.json();
      const business = Array.isArray(businesses.items)
        ? businesses.items[0]
        : businesses[0];
      if (!business) throw new Error("Business not found");
      return business;
    },
    enabled: !!slug,
  });

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <PageContainer size="read">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }

  if (!business) {
    return (
      <PageContainer size="read">
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground">업체 정보를 불러올 수 없습니다</p>
            <Link href="/business-directory">
              <Button className="mt-4">목록으로 돌아가기</Button>
            </Link>
          </CardContent>
        </Card>
      </PageContainer>
    );
  }

  const qrUrl = `/api/businesses/${business.id}/qr`;
  const landingUrl = `${window.location.origin}/biz/${slug}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Print-only header */}
      <div className="hidden print:block text-center py-4">
        <h1 className="text-2xl font-bold">{business.name}</h1>
        <p className="text-muted-foreground mt-2">휴대폰으로 스캔하세요</p>
      </div>

      {/* Screen-only controls */}
      <div className="print:hidden">
        <PageContainer size="read">
          <div className="flex items-center justify-between mb-6">
            <Link href={`/biz/${slug}`}>
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                돌아가기
              </Button>
            </Link>
            <Button onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-2" />
              인쇄하기
            </Button>
          </div>
        </PageContainer>
      </div>

      {/* Print content */}
      <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 py-8 print:min-h-0 print:py-4">
        {/* Business Logo/Name */}
        <div className="mb-8 text-center print:mb-6">
          {business.coverImageUrl ? (
            <img
              src={business.coverImageUrl}
              alt={business.name}
              className="h-24 w-24 mx-auto mb-4 rounded-lg object-cover print:h-20 print:w-20"
            />
          ) : (
            <div className="h-24 w-24 mx-auto mb-4 rounded-lg bg-muted flex items-center justify-center print:h-20 print:w-20">
              <span className="text-2xl font-bold">{business.name[0]}</span>
            </div>
          )}
          <h2 className="text-3xl font-bold print:text-2xl">{business.name}</h2>
        </div>

        {/* QR Code */}
        <div className="mb-8 print:mb-6">
          <img
            src={qrUrl}
            alt="QR Code"
            className="w-64 h-64 mx-auto print:w-56 print:h-56"
          />
        </div>

        {/* Instructions */}
        <div className="text-center max-w-md print:max-w-none">
          <p className="text-lg font-semibold mb-2 print:text-base">
            휴대폰으로 스캔하세요
          </p>
          <p className="text-sm text-muted-foreground print:text-xs">
            QR 코드를 스캔하면 업체 홍보페이지로 이동합니다
          </p>
          <p className="text-xs text-muted-foreground mt-4 print:hidden">
            또는 직접 방문:{" "}
            <a href={landingUrl} className="underline">
              {landingUrl}
            </a>
          </p>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          @page {
            margin: 1cm;
            size: A4;
          }
          body {
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:block {
            display: block !important;
          }
        }
      `}</style>
    </div>
  );
}

